/**
 * The small set of components every screen is built from.
 *
 * Hand-written rather than pulled from shadcn/ui, which the plan named. That
 * library's value is the accessible behaviour of complex widgets — comboboxes,
 * popovers, modal focus management — delivered via Radix. This dashboard needs
 * cards, tables, forms, badges and one modal. The modal is the only piece with
 * real behaviour in it, and the platform now ships that: `<dialog>` gives a
 * top layer, focus containment, Escape-to-close and an inert background,
 * implemented by the browser. Taking Radix, CVA, clsx and tailwind-merge for
 * the rest would be four dependencies to render a `<table>`.
 */

import { useEffect, useRef, type ReactNode } from "react";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

// --- layout ------------------------------------------------------------------

export function Card({
  title,
  action,
  children,
  className,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        "rounded-xl border border-line bg-raised shadow-sm",
        className
      )}
    >
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          {typeof title === "string" ? (
            <h2 className="text-sm font-semibold">{title}</h2>
          ) : (
            title
          )}
          {action}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "default" | "warn" | "bad";
}) {
  return (
    <div className="rounded-xl border border-line bg-raised p-4">
      <div className="text-xs font-medium tracking-wide text-muted uppercase">{label}</div>
      <div
        className={cx(
          "mt-1 text-2xl font-semibold tabular-nums",
          tone === "warn" && "text-warn",
          tone === "bad" && "text-bad"
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}

// --- controls ----------------------------------------------------------------

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
};

export function Button({
  variant = "secondary",
  size = "md",
  className,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      // `type` defaults to "submit" inside a form, which turns an ordinary
      // action button into an accidental form submission.
      type={rest.type ?? "button"}
      className={cx(
        "inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition",
        "disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm",
        variant === "primary" && "bg-brand text-white hover:opacity-90",
        variant === "secondary" && "border border-line bg-raised hover:bg-surface",
        variant === "ghost" && "text-muted hover:bg-surface hover:text-ink",
        variant === "danger" && "border border-bad/30 bg-bad-soft text-bad hover:bg-bad/10",
        className
      )}
    />
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  id,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
  id: string;
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {/* `role="alert"` so a validation failure is announced rather than only
          rendered — a sighted user sees red, everyone else needs telling. */}
      {error && (
        <p id={errorId} role="alert" className="text-xs text-bad">
          {error}
        </p>
      )}
    </div>
  );
}

export const inputClass =
  "w-full rounded-lg border border-line bg-raised px-3 py-1.5 text-sm " +
  "placeholder:text-muted/70 focus:border-brand";

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(inputClass, props.className)} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cx(inputClass, "resize-y", props.className)} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(inputClass, props.className)} />;
}

// --- feedback ----------------------------------------------------------------

export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: "neutral" | "good" | "warn" | "bad" | "brand";
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        tone === "neutral" && "bg-surface text-muted ring-1 ring-line",
        tone === "good" && "bg-good/10 text-good",
        tone === "warn" && "bg-warn/10 text-warn",
        tone === "bad" && "bg-bad/10 text-bad",
        tone === "brand" && "bg-brand-soft text-brand"
      )}
    >
      {children}
    </span>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-line px-6 py-10 text-center">
      <p className="text-sm font-medium">{title}</p>
      {children && <div className="mt-1 text-sm text-muted">{children}</div>}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div role="alert" className="rounded-lg border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
      {message}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return (
    // `role="status"` rather than a bare spinner: a screen reader otherwise
    // announces nothing at all while the page sits empty.
    <div role="status" className="py-8 text-center text-sm text-muted">
      {label}
    </div>
  );
}

// --- modal -------------------------------------------------------------------

export function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // `showModal()`, not the `open` attribute. Only the former puts the
    // element in the top layer and makes the rest of the page inert — setting
    // `open` renders a non-modal dialog that looks identical and traps
    // nothing.
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label={title}
      // Fired by Escape as well as by close(), so this is what keeps React
      // state in step with a dismissal the browser handled itself.
      onClose={onClose}
      onClick={(event) => {
        // Clicking the backdrop. The backdrop is not a child element, so a
        // click on it targets the dialog itself — which is how this
        // distinguishes it from a click on the content inside.
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="min-w-[20rem] p-5">
        <h2 className="text-base font-semibold">{title}</h2>
        <div className="mt-3">{children}</div>
      </div>
    </dialog>
  );
}
